// SCREENS-S1 (browser-accounts §3): the company's browser logins. A row: label, handle, owner,
// status, its grants as chips (remove on hover). For the owner or an admin: a Log in button (a login
// browser on the account, selected in the viewer in control; SCREENS-VIEWER) and hover icons Grant
// (to a DE or a seat) and Remove.
// "Add login" is anyone's: the person who adds it owns it.
// SAVED-LOGIN-SHARING: rows grouped by site (several Instagram logins read together), each with
// its "Use for" note (DEs pick by it), who drives it now ("In use · Riley", one at a time), and a
// Share dialog (owner or admin: several DEs at once, who has it, take a share back).
// PERSISTENT-SCREENS: "Accounts" (the label stays; "profiles" means users) — saved logins agents reuse: a browser per site or a desktop
// home; each row says its site, who added it and when it was last used. Admins set how long a
// screen waits on a person (default 30 min).
import type { OperationInput } from "@companyos/sdk";
import {
  Badge,
  Button,
  Checkbox,
  cn,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  Field,
  FieldLabel,
  InfoTip,
  Input,
  Label,
  NativeSelect,
  NativeSelectOption,
  Textarea,
  toast,
  WorkerBadge,
  workerName,
} from "@companyos/ui";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  GlobeIcon,
  KeyRoundIcon,
  LogInIcon,
  MonitorIcon,
  PencilIcon,
  PlusIcon,
  Trash2Icon,
  UserPlusIcon,
  XIcon,
} from "lucide-react";
import { type FormEvent, useState } from "react";
import type { Api } from "../../api/client";
import { HOVER_STRIP, IconAction } from "../cadence/cadence-parts";
import { companyQuery, digitalEmployeesQuery, keys, seatsQuery } from "../queries";
import { LoadError, Loading } from "../screen-parts";
import { problemText } from "../settings/problem-text";
import { type BrowserAccount, browserAccountsQuery, screensKeys } from "./screens-queries";

type Level = OperationInput<"browserAccounts.grant">["body"]["level"];
const LEVELS: { value: Level; label: string }[] = [
  { value: "use_read", label: "Use · read" },
  { value: "use_act", label: "Use · act" },
  { value: "manage", label: "Manage" },
];

const STATUS: Record<
  BrowserAccount["status"],
  { label: string; variant: "success" | "warning" | "neutral" }
> = {
  live: { label: "Live", variant: "success" },
  needs_you: { label: "Needs you", variant: "warning" },
  expired: { label: "Expired", variant: "neutral" },
  revoked: { label: "Removed", variant: "neutral" },
};

const fail = (e: unknown, what: string) =>
  toast.add({ title: problemText(e, what), type: "error" });

type Modal =
  | { kind: "add" }
  | { kind: "grant"; account: BrowserAccount }
  | { kind: "edit"; account: BrowserAccount }
  | { kind: "remove"; account: BrowserAccount };

/** SAVED-LOGIN-SHARING: rows grouped by site, so several logins for one site read together. */
function bySite(rows: readonly BrowserAccount[]): [string, BrowserAccount[]][] {
  const out = new Map<string, BrowserAccount[]>();
  for (const a of rows) {
    const k = siteOf(a);
    out.set(k, [...(out.get(k) ?? []), a]);
  }
  return [...out];
}

/** "3 days ago" (no seconds: a row's last use). */
function ago(iso: string, now = Date.now()): string {
  const s = Math.round((new Date(iso).getTime() - now) / 1000);
  const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });
  const a = Math.abs(s);
  if (a < 3_600) return rtf.format(Math.round(s / 60), "minute");
  if (a < 86_400) return rtf.format(Math.round(s / 3_600), "hour");
  return rtf.format(Math.round(s / 86_400), "day");
}

const siteOf = (a: BrowserAccount) =>
  a.kind === "desktop" || !a.origin ? "Desktop" : new URL(a.origin).hostname.replace(/^www\./, "");

export function AccountsPanel({
  api,
  co,
  admin = false,
  onLogIn,
  loggingIn,
  ownedBy,
}: {
  api: Api;
  co: string;
  /** Admins set the company's wait for a person. */
  admin?: boolean;
  /** CONNECTIONS-AREA (Team › Connections › Mine): only the logins this membership owns. */
  ownedBy?: string | null;
  /** "Log in": the page opens a login browser on the account and shows it, in control. */
  onLogIn: (account: BrowserAccount) => void;
  /** A login browser is being opened (the buttons wait). */
  loggingIn: boolean;
}) {
  const qc = useQueryClient();
  const accounts = useQuery(browserAccountsQuery(api, co));
  const rows = (accounts.data?.data ?? []).filter(
    (a) => ownedBy === undefined || a.owner.membership_id === ownedBy,
  );
  const [modal, setModal] = useState<Modal | null>(null);
  const refresh = () => qc.invalidateQueries({ queryKey: screensKeys.all(co) });
  const ungrant = useMutation({
    mutationFn: (v: { account: string; grant: string }) =>
      api.client.browserAccounts.ungrant({ params: { co, id: v.account, grant: v.grant } }),
    onSuccess: refresh,
    onError: (e) => fail(e, "Could not remove the grant."),
  });

  return (
    <section aria-labelledby="screens-accounts" className="grid gap-3" data-slot="screens-accounts">
      <div className="flex items-center gap-2">
        <h2 id="screens-accounts" className="text-sm font-semibold">
          Accounts
        </h2>
        <InfoTip label="About accounts">
          Saved logins your agents reuse: a browser per site, or a desktop home. You log in once,
          then share it with DEs; later tasks stay signed in. Several per site are fine — "Use for"
          tells a DE which to pick. One DE uses a login at a time. Kept encrypted on your company's
          computer — DEs and CompanyOS never see passwords.
        </InfoTip>
        {admin ? <WaitSetting api={api} co={co} /> : null}
        <Button
          size="sm"
          variant="outline"
          className={admin ? undefined : "ml-auto"}
          onClick={() => setModal({ kind: "add" })}
        >
          <PlusIcon aria-hidden="true" />
          Add account
        </Button>
      </div>
      {accounts.isPending ? (
        <Loading label="Loading accounts" />
      ) : accounts.isError ? (
        <LoadError what="accounts" error={accounts.error} />
      ) : rows.length === 0 ? (
        <EmptyState variant="compact" icon={KeyRoundIcon} title="No accounts yet" />
      ) : (
        <div className="grid gap-3">
          {bySite(rows).map(([site, list]) => (
            <div key={site} className="grid gap-1" data-slot="account-group" data-site={site}>
              <h3 className="flex items-center gap-1.5 px-1 text-xs font-medium text-muted-foreground">
                {site}
                <span className="text-faint">{list.length}</span>
              </h3>
              <ul className="divide-y divide-border rounded-lg border border-border">
                {list.map((a) => (
                  <li
                    key={a.id}
                    data-slot="account-row"
                    data-handle={a.handle}
                    className="group/row flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1.5 px-3 py-2.5"
                  >
                    <div className="grid min-w-0 flex-1 gap-0.5">
                      <div className="flex min-w-0 items-center gap-2">
                        {a.kind === "desktop" ? (
                          <MonitorIcon
                            aria-hidden="true"
                            className="size-3.5 shrink-0 text-faint"
                          />
                        ) : (
                          <GlobeIcon aria-hidden="true" className="size-3.5 shrink-0 text-faint" />
                        )}
                        <span className="truncate font-medium">{a.label}</span>
                        <Badge variant={STATUS[a.status].variant}>{STATUS[a.status].label}</Badge>
                        {a.in_use_by ? (
                          <Badge variant="info" data-slot="account-in-use">
                            In use · {workerName(a.in_use_by.name)}
                            {a.waiting ? ` · ${a.waiting} waiting` : ""}
                          </Badge>
                        ) : null}
                      </div>
                      {a.use_for ? (
                        <span className="truncate text-xs" data-slot="account-use-for">
                          {a.use_for}
                        </span>
                      ) : null}
                      <span
                        className="truncate text-xs text-muted-foreground"
                        data-slot="profile-meta"
                      >
                        {siteOf(a)} · {a.handle} · {a.owner.name}
                        {a.created_by && a.created_by.membership_id !== a.owner.membership_id
                          ? ` · added by ${workerName(a.created_by.name)}`
                          : ""}
                        {" · "}
                        {a.last_used_at ? `used ${ago(a.last_used_at)}` : "never used"}
                      </span>
                    </div>
                    <div className="flex min-w-0 flex-wrap items-center gap-1">
                      {a.grants.map((g) => (
                        <span
                          key={g.id}
                          data-slot="grant-chip"
                          title={`${g.level === "use_read" ? "Read" : g.level === "use_act" ? "Act" : "Manage"}${g.granted_by ? ` · shared by ${g.granted_by.name}` : ""}`}
                          className="group/chip inline-flex h-6 max-w-full items-center gap-1 rounded-sm bg-secondary px-1.5 text-xs"
                        >
                          {g.subject_kind === "membership" ? (
                            <WorkerBadge kind="de" size="sm" />
                          ) : null}
                          <span className="truncate">
                            {g.subject_kind === "membership"
                              ? workerName(g.subject_name)
                              : g.subject_name}
                          </span>
                          {a.can_manage ? (
                            <button
                              type="button"
                              aria-label={`Remove ${g.subject_name}'s access`}
                              className="hidden text-faint group-hover/chip:inline-flex group-focus-within/chip:inline-flex hover:text-danger-text pointer-coarse:inline-flex"
                              onClick={() => ungrant.mutate({ account: a.id, grant: g.id })}
                            >
                              <XIcon aria-hidden="true" className="size-3" />
                            </button>
                          ) : null}
                        </span>
                      ))}
                    </div>
                    {a.can_manage ? (
                      <Button
                        size="sm"
                        variant="outline"
                        aria-label={`Log in to ${a.label}`}
                        disabled={loggingIn}
                        onClick={() => onLogIn(a)}
                      >
                        <LogInIcon aria-hidden="true" />
                        Log in
                      </Button>
                    ) : null}
                    {a.can_manage ? (
                      <div className={cn(HOVER_STRIP)}>
                        <IconAction
                          label={`Share ${a.label}`}
                          tip="Share with DEs"
                          onClick={() => setModal({ kind: "grant", account: a })}
                        >
                          <UserPlusIcon />
                        </IconAction>
                        <IconAction
                          label={`Edit ${a.label}`}
                          tip="Name and use for"
                          onClick={() => setModal({ kind: "edit", account: a })}
                        >
                          <PencilIcon />
                        </IconAction>
                        <IconAction
                          label={`Remove ${a.label}`}
                          tip="Remove"
                          danger
                          onClick={() => setModal({ kind: "remove", account: a })}
                        >
                          <Trash2Icon />
                        </IconAction>
                      </div>
                    ) : null}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
      {modal?.kind === "add" ? (
        <AddLoginDialog api={api} co={co} onClose={() => setModal(null)} onDone={refresh} />
      ) : modal?.kind === "grant" ? (
        <GrantDialog
          api={api}
          co={co}
          account={modal.account}
          onClose={() => setModal(null)}
          onDone={refresh}
        />
      ) : modal?.kind === "edit" ? (
        <EditDialog
          api={api}
          co={co}
          account={modal.account}
          onClose={() => setModal(null)}
          onDone={refresh}
        />
      ) : modal?.kind === "remove" ? (
        <RemoveDialog
          api={api}
          co={co}
          account={modal.account}
          onClose={() => setModal(null)}
          onDone={refresh}
        />
      ) : null}
    </section>
  );
}

type DialogProps = { api: Api; co: string; onClose: () => void; onDone: () => void };

/**
 * Admins: how long a screen waiting on a person stays open (`settings.screens`, default 30 min).
 * The PATCH replaces the whole settings object: read them fresh first.
 */
function WaitSetting({ api, co }: { api: Api; co: string }) {
  const qc = useQueryClient();
  const company = useQuery(companyQuery(api, co));
  const current = company.data?.settings.screens?.needs_you_wait_min ?? 30;
  const save = useMutation({
    mutationFn: async (min: number) => {
      const d = await qc.fetchQuery({ ...companyQuery(api, co), staleTime: 0 });
      return api.client.companies.update({
        params: { co },
        body: {
          settings: { ...d.settings, screens: { ...d.settings.screens, needs_you_wait_min: min } },
        },
      });
    },
    onSuccess: (d) => qc.setQueryData(keys.company(co), d),
    onError: (e) => fail(e, "Not saved."),
  });
  return (
    <div className="ml-auto flex items-center gap-1 text-xs text-muted-foreground">
      <label htmlFor="screens-wait">Wait for a person</label>
      <InfoTip label="About the wait">
        A screen asking for a login or a 2FA code stays open this long. The person can press Extend.
      </InfoTip>
      <NativeSelect
        id="screens-wait"
        size="sm"
        value={String(current)}
        disabled={company.isPending || save.isPending}
        onChange={(e) => save.mutate(Number(e.target.value))}
      >
        {[...new Set([10, 15, 30, 45, 60, 90, 120, current])]
          .sort((x, y) => x - y)
          .map((m) => (
            <NativeSelectOption key={m} value={String(m)}>
              {m} min
            </NativeSelectOption>
          ))}
      </NativeSelect>
    </div>
  );
}

function AddLoginDialog({ api, co, onClose, onDone }: DialogProps) {
  const [kind, setKind] = useState<"browser" | "desktop">("browser");
  const [handle, setHandle] = useState("");
  const [origin, setOrigin] = useState("https://");
  const [label, setLabel] = useState("");
  const [useFor, setUseFor] = useState("");
  const add = useMutation({
    mutationFn: () =>
      api.client.browserAccounts.create({
        params: { co },
        body: {
          handle: handle.trim().toLowerCase(),
          label: label.trim(),
          ...(useFor.trim() ? { use_for: useFor.trim() } : {}),
          ...(kind === "desktop" ? { kind } : { origin: origin.trim() }),
        },
      }),
    onSuccess: () => {
      onDone();
      onClose();
    },
    onError: (e) => fail(e, "Could not add the account."),
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    add.mutate();
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={submit} className="grid gap-4">
          <DialogHeader>
            <DialogTitle>Add account</DialogTitle>
          </DialogHeader>
          <Field>
            <FieldLabel htmlFor="login-kind" className="flex items-center gap-1">
              Type
              <InfoTip label="About account types">
                Browser: one site's login. Desktop: a home folder that keeps app logins.
              </InfoTip>
            </FieldLabel>
            <NativeSelect
              id="login-kind"
              className="w-full"
              value={kind}
              onChange={(e) => setKind(e.target.value === "desktop" ? "desktop" : "browser")}
            >
              <NativeSelectOption value="browser">Browser</NativeSelectOption>
              <NativeSelectOption value="desktop">Desktop</NativeSelectOption>
            </NativeSelect>
          </Field>
          <Field>
            <FieldLabel htmlFor="login-label">Name</FieldLabel>
            <Input
              id="login-label"
              required
              maxLength={80}
              placeholder="Company · Instagram main"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
            />
          </Field>
          <Field>
            <FieldLabel htmlFor="login-handle" className="flex items-center gap-1">
              Handle
              <InfoTip label="About the handle">
                site/name, lowercase — agents ask for it by this.
              </InfoTip>
            </FieldLabel>
            <Input
              id="login-handle"
              required
              placeholder={kind === "desktop" ? "desktop/main" : "instagram/main"}
              pattern="[a-z0-9][a-z0-9._\-]{0,39}/[a-z0-9][a-z0-9._\-]{0,39}"
              value={handle}
              onChange={(e) => setHandle(e.target.value)}
            />
          </Field>
          {kind === "browser" ? (
            <Field>
              <FieldLabel htmlFor="login-origin">Site</FieldLabel>
              <Input
                id="login-origin"
                required
                type="url"
                pattern="https://.+"
                value={origin}
                onChange={(e) => setOrigin(e.target.value)}
              />
            </Field>
          ) : null}
          <UseForField value={useFor} onChange={setUseFor} />
          <DialogFooter>
            <DialogClose render={<Button type="button" variant="outline" />}>Cancel</DialogClose>
            <Button type="submit" disabled={add.isPending}>
              Add account
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function GrantDialog({
  api,
  co,
  account,
  onClose,
  onDone,
}: DialogProps & { account: BrowserAccount }) {
  const [kind, setKind] = useState<"membership" | "seat">("membership");
  const [subject, setSubject] = useState("");
  const [picked, setPicked] = useState<ReadonlySet<string>>(new Set());
  const [level, setLevel] = useState<Level>("use_act");
  const des = useInfiniteQuery(digitalEmployeesQuery(api, co));
  const seats = useInfiniteQuery(seatsQuery(api, co));
  const qc = useQueryClient();
  const live = useQuery(browserAccountsQuery(api, co));
  // The dialog's own copy follows the list (a share or a take-back shows at once).
  const acc = live.data?.data.find((x) => x.id === account.id) ?? account;
  const has = new Map(
    acc.grants.filter((g) => g.subject_kind === "membership").map((g) => [g.subject_id, g]),
  );
  const deList = (des.data?.pages.flatMap((p) => p.data) ?? []).map((d) => ({
    id: d.membership_id,
    label: workerName(d.display_name),
  }));
  const seatList = (seats.data?.pages.flatMap((p) => p.data) ?? []).map((s) => ({
    id: s.id,
    label: s.title,
  }));
  const chosenSeat = seatList.some((o) => o.id === subject) ? subject : (seatList[0]?.id ?? "");
  // A DE that already has this level is skipped (a different level changes it).
  const targets =
    kind === "membership"
      ? [...picked].filter((id) => has.get(id)?.level !== level)
      : chosenSeat
        ? [chosenSeat]
        : [];
  const grant = useMutation({
    mutationFn: async () => {
      // One share per DE; each is its own grant (visible and revocable on its own). Each done
      // one leaves the selection, so a retry after a failure sends only the rest.
      for (const id of targets) {
        await api.client.browserAccounts.grant({
          params: { co, id: account.id },
          body: { subject_kind: kind, subject_id: id, level },
        });
        if (kind === "membership") toggle(id, false);
      }
    },
    onSuccess: () => {
      onDone();
      onClose();
    },
    onError: (e) => {
      onDone();
      fail(e, "Could not share the login.");
    },
  });
  const ungrant = useMutation({
    mutationFn: (grantId: string) =>
      api.client.browserAccounts.ungrant({ params: { co, id: account.id, grant: grantId } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: screensKeys.all(co) }),
    onError: (e) => fail(e, "Could not remove the share."),
  });
  const toggle = (id: string, on: boolean) =>
    setPicked((s) => {
      const n = new Set(s);
      if (on) n.add(id);
      else n.delete(id);
      return n;
    });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (targets.length) grant.mutate();
  };
  const levelName = (l: string) => (l === "use_read" ? "Read" : l === "use_act" ? "Act" : "Manage");
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-slot="share-dialog">
        <form onSubmit={submit} className="grid gap-4">
          <DialogHeader>
            <DialogTitle>Share {account.label}</DialogTitle>
            <DialogDescription className="flex items-center gap-1">
              {account.owner.name}'s login · {siteOf(account)}
              <InfoTip label="About sharing">
                Log in once, share with any DE. They reuse the saved session from any screen; they
                never see the password. One DE uses it at a time — the others wait their turn.
              </InfoTip>
            </DialogDescription>
          </DialogHeader>
          {acc.grants.length ? (
            <div className="grid gap-1" data-slot="share-current">
              <span className="text-xs font-medium text-muted-foreground">Shared with</span>
              <ul className="flex flex-wrap gap-1">
                {acc.grants.map((g) => (
                  <li
                    key={g.id}
                    className="inline-flex h-6 items-center gap-1 rounded-sm bg-secondary px-1.5 text-xs"
                    title={g.granted_by ? `Shared by ${g.granted_by.name}` : undefined}
                  >
                    {g.subject_kind === "membership" ? <WorkerBadge kind="de" size="sm" /> : null}
                    <span className="truncate">
                      {g.subject_kind === "membership"
                        ? workerName(g.subject_name)
                        : g.subject_name}
                    </span>
                    <span className="text-faint">{levelName(g.level)}</span>
                    <button
                      type="button"
                      aria-label={`Stop sharing with ${g.subject_name}`}
                      className="inline-flex text-faint hover:text-danger-text"
                      onClick={() => ungrant.mutate(g.id)}
                    >
                      <XIcon aria-hidden="true" className="size-3" />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          <Field>
            <FieldLabel htmlFor="grant-kind">With</FieldLabel>
            <NativeSelect
              id="grant-kind"
              className="w-full"
              value={kind}
              onChange={(e) => setKind(e.target.value === "seat" ? "seat" : "membership")}
            >
              <NativeSelectOption value="membership">Digital employees</NativeSelectOption>
              <NativeSelectOption value="seat">A seat</NativeSelectOption>
            </NativeSelect>
          </Field>
          {kind === "membership" ? (
            <fieldset className="grid gap-1" data-slot="share-des">
              <legend className="sr-only">Digital employees</legend>
              {deList.map((d) => {
                const g = has.get(d.id);
                const id = `share-de-${d.id}`;
                return (
                  <div key={d.id} className="flex h-8 items-center gap-2 rounded-md px-1">
                    <Checkbox
                      id={id}
                      checked={picked.has(d.id)}
                      onCheckedChange={(on) => toggle(d.id, on === true)}
                    />
                    <Label htmlFor={id} className="flex min-w-0 items-center gap-1.5 font-normal">
                      <WorkerBadge kind="de" size="sm" />
                      <span className="truncate">{d.label}</span>
                    </Label>
                    {g ? (
                      <span className="ml-auto text-xs text-muted-foreground">
                        has {levelName(g.level)}
                      </span>
                    ) : null}
                  </div>
                );
              })}
            </fieldset>
          ) : (
            <Field>
              <FieldLabel htmlFor="grant-subject">Seat</FieldLabel>
              <NativeSelect
                id="grant-subject"
                className="w-full"
                value={chosenSeat}
                onChange={(e) => setSubject(e.target.value)}
              >
                {seatList.map((o) => (
                  <NativeSelectOption key={o.id} value={o.id}>
                    {o.label}
                  </NativeSelectOption>
                ))}
              </NativeSelect>
            </Field>
          )}
          <Field>
            <FieldLabel htmlFor="grant-level" className="flex items-center gap-1">
              Level
              <InfoTip label="About levels">
                Read: look only. Act: post, reply, click. Manage: also grant it on.
              </InfoTip>
            </FieldLabel>
            <NativeSelect
              id="grant-level"
              className="w-full"
              value={level}
              onChange={(e) => setLevel(e.target.value as Level)}
            >
              {LEVELS.map((l) => (
                <NativeSelectOption key={l.value} value={l.value}>
                  {l.label}
                </NativeSelectOption>
              ))}
            </NativeSelect>
          </Field>
          <DialogFooter>
            <DialogClose render={<Button type="button" variant="outline" />}>Cancel</DialogClose>
            <Button type="submit" disabled={targets.length === 0 || grant.isPending}>
              Share
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** SAVED-LOGIN-SHARING: "when to use it", read by DEs to pick between logins for one site. */
function UseForField({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <Field>
      <FieldLabel htmlFor="login-use-for" className="flex items-center gap-1">
        Use for
        <InfoTip label="About use for">
          DEs read this to pick the right login when there are several for one site.
        </InfoTip>
      </FieldLabel>
      <Textarea
        id="login-use-for"
        rows={2}
        maxLength={280}
        placeholder="Client posts, Mon–Wed"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </Field>
  );
}

function EditDialog({
  api,
  co,
  account,
  onClose,
  onDone,
}: DialogProps & { account: BrowserAccount }) {
  const [label, setLabel] = useState(account.label);
  const [useFor, setUseFor] = useState(account.use_for ?? "");
  const save = useMutation({
    mutationFn: () =>
      api.client.browserAccounts.update({
        params: { co, id: account.id },
        body: { label: label.trim(), use_for: useFor.trim() || null },
      }),
    onSuccess: () => {
      onDone();
      onClose();
    },
    onError: (e) => fail(e, "Not saved."),
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    save.mutate();
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={submit} className="grid gap-4">
          <DialogHeader>
            <DialogTitle>Edit {account.label}</DialogTitle>
            <DialogDescription>
              {siteOf(account)} · {account.handle}
            </DialogDescription>
          </DialogHeader>
          <Field>
            <FieldLabel htmlFor="login-edit-label">Name</FieldLabel>
            <Input
              id="login-edit-label"
              required
              maxLength={80}
              value={label}
              onChange={(e) => setLabel(e.target.value)}
            />
          </Field>
          <UseForField value={useFor} onChange={setUseFor} />
          <DialogFooter>
            <DialogClose render={<Button type="button" variant="outline" />}>Cancel</DialogClose>
            <Button type="submit" disabled={!label.trim() || save.isPending}>
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function RemoveDialog({
  api,
  co,
  account,
  onClose,
  onDone,
}: DialogProps & { account: BrowserAccount }) {
  const remove = useMutation({
    mutationFn: () => api.client.browserAccounts.revoke({ params: { co, id: account.id } }),
    onSuccess: () => {
      onDone();
      onClose();
    },
    onError: (e) => fail(e, "Could not remove the login."),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Remove {account.label}?</DialogTitle>
          <DialogDescription>
            Its live screens end and its saved session is destroyed on the computer.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <DialogClose render={<Button type="button" variant="outline" />}>Cancel</DialogClose>
          <Button variant="destructive" disabled={remove.isPending} onClick={() => remove.mutate()}>
            Remove
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
